import { useEffect, useMemo, useState } from 'react';

export function useCanvasScale(
  containerRef: React.RefObject<HTMLElement | null>,
  designWidth: number,
  designHeight: number,
  targetWidth?: number,
  targetHeight?: number
) {
  const [scale, setScale] = useState(1);

  // Compute scale against container while keeping internal coordinate system fixed
  useEffect(() => {
    const el = containerRef.current as HTMLElement | null;
    if (!el) return;

    let raf = 0;
    const compute = () => {
      const rect = el.getBoundingClientRect();
      const cw = Math.max(1, Math.floor(rect.width));
      const ch = Math.max(1, Math.floor(rect.height));

      // On mobile, if a targetWidth/targetHeight (e.g. the template size) is provided, 
      // we scale the whole canvas so that the TARGET fits the screen width.
      // This makes the template appear full-width.
      const isMobile = window.innerWidth < 768;
      const margin = isMobile ? 8 : 0; // 8px total gap (4px per side)
      const availW = Math.max(1, cw - margin);
      const availH = Math.max(1, ch - margin);

      let fitScale = 1;
      if (isMobile && targetWidth && targetHeight) {
        // Fit based on target dimensions to make it "big"
        // Reverted to standard fit logic (full view) as per user request
        fitScale = Math.min(availW / targetWidth, availH / targetHeight);
      } else {
        // Standard fit logic for the entire canvas
        fitScale = Math.min(availW / designWidth, availH / designHeight);
      }

      setScale(fitScale > 0 ? fitScale : 1);
    };

    compute();

    const ro = new ResizeObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    });
    ro.observe(el);

    const onWindow = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    window.addEventListener('resize', onWindow);
    window.addEventListener('orientationchange', onWindow);

    return () => {
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', onWindow);
      window.removeEventListener('orientationchange', onWindow);
    };
  }, [containerRef, designWidth, designHeight, targetWidth, targetHeight]);

  const viewportStyle = useMemo(() => ({
    width: `${Math.max(1, Math.floor(designWidth * scale))}px`,
    height: `${Math.max(1, Math.floor(designHeight * scale))}px`,
  }), [scale, designWidth, designHeight]);

  return { scale, viewportStyle } as const;
}
